import type { QueryClient } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { uploadAppIcon } from '@keboola/api-client/developerPortal';
import type {
  App,
  CreateServiceAccountInput,
  UpdateAppInput,
  UpdateVendorInput,
  User,
  VendorListItem,
  Version,
} from '@keboola/api-client/developerPortal/types';

import { api, session } from '../../api';
import { fetchAllPages } from '../../paging';
import { adminKeys } from '../admin/queries';
import type { NewAppInput } from '../apps/create';
import { setDefaultValuesOnCreation } from '../apps/create';
import { appQueryKeys } from '../apps/queries';

/**
 * Query keys for vendor-scoped surfaces.
 *
 * - Every key carries the vendor id, so switching vendor never shows the previous vendor's cache.
 */
const vendorKeys = {
  /** Everything under one vendor, for the blanket invalidation on leaving. */
  scope: (vendorId: string) => ['vendor', vendorId] as const,
  apps: (vendorId: string) => ['vendor', vendorId, 'apps'] as const,
  app: (vendorId: string, appId: string) => ['vendor', vendorId, 'apps', appId] as const,
  appVersions: (vendorId: string, appId: string) =>
    ['vendor', vendorId, 'apps', appId, 'versions'] as const,
  users: (vendorId: string) => ['vendor', vendorId, 'users'] as const,
  serviceAccounts: (vendorId: string) => ['vendor', vendorId, 'service-accounts'] as const,
  profile: ['profile'] as const,
  vendor: (vendorId: string) => ['vendors', vendorId] as const,
  allVendors: ['vendors'] as const,
} as const;

/**
 * Invalidates both member lists after any write to a vendor's members.
 *
 * - `users` and `serviceAccounts` are one collection, `/vendors/{id}/users`, ± `service=1`.
 * - Service accounts also appear in the developers table (see `useVendorUsers`).
 */
const invalidateMembers = (queryClient: QueryClient, vendorId: string) => {
  void queryClient.invalidateQueries({ queryKey: vendorKeys.users(vendorId) });
  void queryClient.invalidateQueries({ queryKey: vendorKeys.serviceAccounts(vendorId) });
};

/**
 * Every component belonging to the current vendor.
 *
 * - Fetches all pages: apps-api cannot filter, and the list page filters text client-side.
 */
export const useVendorApps = (vendorId: string) =>
  useQuery({
    queryKey: vendorKeys.apps(vendorId),
    queryFn: ({ signal }): Promise<App[]> =>
      fetchAllPages(
        (range) => api.apps.getApps(vendorId, range, signal),
        "this vendor's components",
      ),
  });

export const useVendorApp = (vendorId: string, appId: string) =>
  useQuery({
    queryKey: vendorKeys.app(vendorId, appId),
    queryFn: ({ signal }) => api.apps.getApp(vendorId, appId, signal),
  });

/** The signed-in user's MFA enrolment state. Drives the MFA gate. */
export const useProfile = () =>
  useQuery({
    queryKey: vendorKeys.profile,
    queryFn: ({ signal }) => api.auth.getProfile(signal),
  });

/**
 * Every vendor, for the select-vendor gate's picker.
 *
 * - `GET /vendors` is public, so it works before the user belongs to any vendor.
 * - Elsewhere, {@link useVendor} reads the one known row.
 */
export const useAllVendors = () =>
  useQuery({
    queryKey: vendorKeys.allVendors,
    // `VendorListItem`, not `Vendor`: `GET /vendors` returns `isTrusted` as a raw tinyint.
    queryFn: ({ signal }): Promise<VendorListItem[]> => api.vendors.getVendors({}, signal),
  });

/** One vendor, for the pages that already know which one they are on. */
export const useVendor = (vendorId: string) =>
  useQuery({
    queryKey: vendorKeys.vendor(vendorId),
    queryFn: ({ signal }) => api.vendors.getVendor(vendorId, signal),
  });

export const useCreateApp = (vendorId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: NewAppInput) =>
      api.apps.createApp(vendorId, setDefaultValuesOnCreation(input)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: vendorKeys.apps(vendorId), exact: false });
    },
  });
};

/**
 * Saves the component definition.
 *
 * - Also invalidates `appQueryKeys`: the public pages cache the edited fields for 60 s.
 */
export const useUpdateApp = (vendorId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ appId, input }: { appId: string; input: UpdateAppInput }) =>
      api.apps.updateApp(vendorId, appId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: vendorKeys.apps(vendorId), exact: false });
      void queryClient.invalidateQueries({ queryKey: appQueryKeys.published, exact: false });
    },
  });
};

/** Every stored version of a component. Admins read it too, under the component's vendor. */
export const useAppVersions = (vendorId: string, appId: string) =>
  useQuery({
    queryKey: vendorKeys.appVersions(vendorId, appId),
    queryFn: ({ signal }): Promise<Version[]> =>
      fetchAllPages(
        (range) => api.apps.getAppVersions(vendorId, appId, range, signal),
        "this component's versions",
      ),
    // Admin edits do not invalidate this vendor-scoped key, so a cached list can be stale.
    refetchOnMount: 'always',
  });

/**
 * Reverts a component to one of its versions.
 *
 * - apps-api writes a new version with every setting of the chosen one.
 * - Invalidates the vendor, admin and public views of the component.
 */
export const useRevertAppVersion = (vendorId: string, appId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (version: number) => api.apps.rollbackAppVersion(vendorId, appId, String(version)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: vendorKeys.apps(vendorId), exact: false });
      void queryClient.invalidateQueries({ queryKey: adminKeys.all, exact: false });
      void queryClient.invalidateQueries({ queryKey: appQueryKeys.published, exact: false });
    },
  });
};

/**
 * Replaces a component's icon.
 *
 * - `uploadAppIcon` PUTs to S3 outside the client: S3 rejects a presigned URL plus `Authorization`.
 * - Invalidation is best-effort: apps-api resizes async, so a refetch may return the old icon.
 */
export const useUploadAppIcon = (vendorId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ appId, png }: { appId: string; png: Blob }) => {
      const { link } = await api.apps.createAppIconUploadLink(vendorId, appId);
      // `link` is optional in the spec; a silent success would falsely report the icon replaced.
      if (link === undefined) throw new Error('apps-api returned no upload link for the icon.');
      await uploadAppIcon(link, png);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: vendorKeys.apps(vendorId), exact: false });
    },
  });
};

export const useDeleteApp = (vendorId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (appId: string) => api.apps.deleteApp(vendorId, appId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: vendorKeys.apps(vendorId), exact: false });
      // The public pages cache apps under their own key family.
      void queryClient.invalidateQueries({ queryKey: appQueryKeys.published, exact: false });
    },
  });
};

export const useRequestPublishing = (vendorId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (appId: string) => api.apps.requestAppPublishing(vendorId, appId),
    onSuccess: () => {
      // `exact: false`: refreshes the list and the open detail view.
      void queryClient.invalidateQueries({ queryKey: vendorKeys.apps(vendorId), exact: false });
    },
  });
};

/**
 * Marks a component deprecated.
 *
 * - ⚠️ A vendor cannot undo it: no un-deprecate endpoint, and `isDeprecated` is admin-only.
 * - Also clears `isPublic` service-side, hence the public catalog invalidation.
 */
export const useDeprecateApp = (vendorId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ appId, expiredOn }: { appId: string; expiredOn?: string }) =>
      // Omitted, not `''`: apps-api answers a non-date `expiredOn` with an opaque 400.
      api.apps.deprecateApp(vendorId, appId, expiredOn === undefined ? {} : { expiredOn }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: vendorKeys.apps(vendorId), exact: false });
      void queryClient.invalidateQueries({ queryKey: appQueryKeys.published, exact: false });
    },
  });
};

/**
 * Starts TOTP enrolment and returns the shared secret to render as a QR.
 *
 * - `ensureFresh()` first (as in `useConfirmMfa`): `/auth/mfa*` send the token in the body, so the
 *   middleware's refresh-and-replay cannot recover a stale one.
 */
export const useBeginMfaEnrolment = () =>
  useMutation({
    mutationFn: async (): Promise<string> => {
      await session.ensureFresh();
      const { secretCode } = await api.auth.setupMfa();
      // Optional in the spec; throwing renders the error alert instead of an inert button.
      if (secretCode === undefined || secretCode === '') {
        throw new Error('The portal did not return a setup key.');
      }
      return secretCode;
    },
  });

export const useConfirmMfa = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (code: string) => {
      await session.ensureFresh();
      await api.auth.confirmMfa(code);
    },
    onSuccess: () => {
      // Re-read the profile so the MFA gate lets the user through.
      void queryClient.invalidateQueries({ queryKey: vendorKeys.profile });
    },
  });
};

/** Files a request to join a vendor; the user stays behind the gate until an admin approves. */
export const useRequestJoinVendor = () =>
  useMutation({
    mutationFn: (vendorId: string) => api.vendors.requestVendorMembership(vendorId),
  });

/**
 * Registers a vendor. Its temporary `_v` id is unselectable until an admin approves it, so the
 * user stays behind the gate.
 */
export const useCreateVendor = () =>
  useMutation({
    mutationFn: (input: { name: string; email: string; address: string }) =>
      api.vendors.createVendor(input),
  });

/** Updates a vendor; invalidates the public vendor list, where the details are read back from. */
export const useUpdateVendor = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ vendorId, input }: { vendorId: string; input: UpdateVendorInput }) =>
      api.vendors.updateVendor(vendorId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: vendorKeys.allVendors });
    },
  });
};

/**
 * The vendor's members, service accounts included.
 *
 * - ⚠️ Same `/vendors/{id}/users` collection; only `service=1` separates them.
 * - Intentional: hiding them would make an account un-removable from the one screen that lists it.
 */
export const useVendorUsers = (vendorId: string) =>
  useQuery({
    queryKey: vendorKeys.users(vendorId),
    queryFn: ({ signal }): Promise<User[]> => api.vendors.getVendorUsers(vendorId, {}, signal),
  });

export const useInviteDeveloper = (vendorId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (email: string) => api.vendors.sendVendorInvitation(vendorId, email),
    onSuccess: () => {
      // The invitee appears in the list immediately, before accepting.
      void queryClient.invalidateQueries({ queryKey: vendorKeys.users(vendorId) });
    },
  });
};

/**
 * Removes a member — or leaves the vendor, when the member is the caller.
 *
 * - `isSelf` forces a token rotation: leaving changes the id token's `profile` claim.
 * - The rotation re-derives `currentVendor` in `AuthProvider`, moving the user on or to the gate.
 */
export const useRemoveVendorUser = (vendorId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ username, isSelf }: { username: string; isSelf: boolean }) => {
      await api.vendors.removeVendorUser(vendorId, username);
      if (isSelf) await session.refresh();
    },
    onSuccess: (_result, { isSelf }) => {
      // Leaving: nothing cached under the old vendor is trustworthy.
      if (isSelf) {
        void queryClient.invalidateQueries({ queryKey: vendorKeys.scope(vendorId), exact: false });
        return;
      }
      // The developers table lists service accounts too, so both lists are re-read.
      invalidateMembers(queryClient, vendorId);
    },
  });
};

export const useServiceAccounts = (vendorId: string) =>
  useQuery({
    queryKey: vendorKeys.serviceAccounts(vendorId),
    // `User[]`: `ServiceAccount` is the creation result, the only response carrying a password.
    queryFn: ({ signal }): Promise<User[]> => api.vendors.getServiceAccounts(vendorId, {}, signal),
  });

/**
 * Creates a service account.
 *
 * - The one-time `password` is returned, never cached: render it once and let it go.
 */
export const useCreateServiceAccount = (vendorId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateServiceAccountInput) =>
      api.vendors.createServiceAccount(vendorId, input),
    onSuccess: () => {
      invalidateMembers(queryClient, vendorId);
    },
  });
};

export const useDeleteServiceAccount = (vendorId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    // A service account is a `/vendors/{vendor}/users` row, so it is removed like a member.
    mutationFn: (username: string) => api.vendors.removeVendorUser(vendorId, username),
    onSuccess: () => {
      invalidateMembers(queryClient, vendorId);
    },
  });
};
